import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";

function EmailSendingSettings() {
  // ----------------------------
  // Domain + sender setup state (existing)
  // ----------------------------
  const [domain, setDomain] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");

  const [dnsRecords, setDnsRecords] = useState(null);

  const [configStatus, setConfigStatus] = useState("not_configured"); // not_configured | pending | verified

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ----------------------------
  // Sender profiles state
  // ----------------------------
  const [senders, setSenders] = useState([]);
  const [sendersLoading, setSendersLoading] = useState(false);

  const [showAddSender, setShowAddSender] = useState(false);
  const [newSenderName, setNewSenderName] = useState("");
  const [newSenderEmail, setNewSenderEmail] = useState("");
  const [addingSender, setAddingSender] = useState(false);

  const [settingDefaultSenderId, setSettingDefaultSenderId] = useState(null);
  const [deletingSenderId, setDeletingSenderId] = useState(null);

  // ----------------------------
  // Team assignments UI
  // ----------------------------
  const [teamUsers, setTeamUsers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedSenderId, setSelectedSenderId] = useState("");
  const [assigning, setAssigning] = useState(false);

  // ---------------------------------------
  // New: Sending method + mailbox connections + active mailbox + signature
  // ---------------------------------------
  // sendingMethod: "oauth" | "domain"
  const [sendingMethod, setSendingMethod] = useState("oauth");

  /**
   * Mailboxes:
   * - oauth/mailboxes returns:
   *   {
   *     mailboxes: [
   *       { provider: "google"|"microsoft", email, displayName, connectedAt, isActive }
   *     ],
   *     activeProvider: "google"|"microsoft"|null
   *   }
   *
   * Backwards compatible: if API only returns { status }, we map it into mailboxes[].
   */
  const [mailboxes, setMailboxes] = useState([]);
  const [activeMailboxProvider, setActiveMailboxProvider] = useState(null);

  const [oauthLoading, setOauthLoading] = useState(false);
  const [inboxPermissions, setInboxPermissions] = useState(null);
  const [inboxPermissionsLoading, setInboxPermissionsLoading] = useState(false);
  const [reconnectingInboxProvider, setReconnectingInboxProvider] = useState(null);
  const [switchingMailbox, setSwitchingMailbox] = useState(false);
  const [disconnectingProvider, setDisconnectingProvider] = useState(null);

  // Signature
  const [signature, setSignature] = useState("");
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);

  // ----------------------------
  // Helpers
  // ----------------------------
  const safeText = (v) => (v == null ? "" : String(v));
  const safeErrorText = (e) => {
    const msg = e?.response?.data?.error ?? e?.response?.data ?? e?.message ?? e;
    if (typeof msg === "string") return msg;
    try {
      return JSON.stringify(msg);
    } catch {
      return "Unknown error";
    }
  };

  const providerLabel = (p) => {
    const v = String(p || "").toLowerCase();
    if (v === "microsoft") return "Microsoft";
    if (v === "google") return "Google";
    return p || "Unknown";
  };

  const providerShortLabel = (p) => {
    const v = String(p || "").toLowerCase();
    if (v === "microsoft") return "Outlook";
    if (v === "google") return "Gmail";
    return "Mailbox";
  };

  function normalizeDnsForDisplay(records) {
    if (!records) return [];

    if (Array.isArray(records)) {
      return records
        .map((r) => ({
          type: r.type || r.recordType || "",
          host: r.host || r.host_name || r.name || "",
          value: r.value || r.data || r.recordValue || "",
        }))
        .filter((r) => r.type || r.host || r.value);
    }

    if (typeof records === "object") {
      const maybe = records.dns_records || records.dnsRecords || records;
      if (maybe && typeof maybe === "object") {
        const flat = [];
        Object.keys(maybe).forEach((k) => {
          const r = maybe[k];
          if (!r || typeof r !== "object") return;
          flat.push({
            type: r.type || "",
            host: r.host || r.host_name || r.name || "",
            value: r.value || r.data || r.recordValue || "",
            key: k,
            status: r.status,
          });
        });
        return flat.filter((r) => r.type || r.host || r.value);
      }
    }

    return [];
  }

  const dnsDisplay = useMemo(() => normalizeDnsForDisplay(dnsRecords), [dnsRecords]);

  const derivedStatus = useMemo(() => configStatus, [configStatus]);

  const canShowDomainSetupForm = derivedStatus === "not_configured";
  const hasDomainConfig = derivedStatus !== "not_configured";

  const canShowSendersSection = derivedStatus === "verified";
  const canCheckDomain = derivedStatus === "pending" || derivedStatus === "verified";

  const domainPlaceholder = "mail.yourdomain.com";

  // Build sender lookup map for showing assigned sender labels in users list
  const senderById = useMemo(() => {
    const m = new Map();
    (senders || []).forEach((s) => {
      const id = safeText(s.id || s.senderId || s._id || "");
      if (!id) return;
      m.set(id, s);
    });
    return m;
  }, [senders]);

  // Extract assigned sender id from user with fallback keys
  const getAssignedSenderIdFromUser = (u) => {
    return (
      safeText(u?.assignedSenderId) ||
      safeText(u?.emailSenderId) ||
      safeText(u?.senderProfileId) ||
      safeText(u?.emailSending?.senderId) ||
      safeText(u?.emailSendingSenderId) ||
      ""
    );
  };

  const activeMailbox = useMemo(() => {
    if (!mailboxes?.length) return null;
    const byActiveFlag = mailboxes.find((m) => !!m.isActive);
    if (byActiveFlag) return byActiveFlag;
    if (activeMailboxProvider) return mailboxes.find((m) => String(m.provider) === String(activeMailboxProvider)) || null;
    return mailboxes[0] || null;
  }, [mailboxes, activeMailboxProvider]);

  const anyMailboxConnected = useMemo(() => {
    return (mailboxes || []).some((m) => !!m?.email);
  }, [mailboxes]);

  // For showing "Sending from" clearly in UI
  const sendingFromLabel = useMemo(() => {
    if (sendingMethod === "oauth") {
      if (activeMailbox?.email) {
        return `${activeMailbox.email} (${providerShortLabel(activeMailbox.provider)})`;
      }
      return "Not connected yet";
    }

    // domain mode
    if (configStatus === "verified") {
      const def = (senders || []).find((s) => !!s.isDefault || !!s.default);
      if (def) {
        const nm = safeText(def.fromName || def.name);
        const em = safeText(def.fromEmail || def.email);
        return nm && em ? `${nm} <${em}> (Domain)` : em ? `${em} (Domain)` : "Domain (verified)";
      }
      return safeText(fromEmail) ? `${safeText(fromEmail)} (Domain)` : "Domain (verified)";
    }

    if (configStatus === "pending") return "Domain setup in progress";
    return "Not configured";
  }, [sendingMethod, activeMailbox, configStatus, senders, fromEmail]);

  const replyToLabel = useMemo(() => {
    if (sendingMethod === "oauth") return activeMailbox?.email ? activeMailbox.email : "—";
    return safeText(fromEmail) || "—";
  }, [sendingMethod, activeMailbox, fromEmail]);

  // ----------------------------
  // Initial load
  // ----------------------------
  useEffect(() => {
    (async () => {
      await Promise.all([fetchConfig(), fetchMailboxesSafe(), fetchInboxPermissionsSafe(), fetchSignatureSafe()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------
  // OAuth + Signature
  // ----------------------------
  const fetchMailboxesSafe = async () => {
    setOauthLoading(true);
    try {
      // New endpoint (preferred)
      const res = await api.get("/email/oauth/mailboxes");
      const list = Array.isArray(res.data?.mailboxes) ? res.data.mailboxes : [];
      const activeProvider = res.data?.activeProvider ?? null;

      if (list.length) {
        // Normalize flags
        const normalized = list.map((m) => ({
          provider: safeText(m.provider || "").toLowerCase(),
          email: safeText(m.email || ""),
          displayName: safeText(m.displayName || ""),
          connectedAt: m.connectedAt || null,
          isActive: !!m.isActive,
        }));
        setMailboxes(normalized);
        setActiveMailboxProvider(activeProvider ? safeText(activeProvider).toLowerCase() : null);

        if (normalized.some((x) => x.isActive) || activeProvider) {
          setSendingMethod("oauth");
        }
        return;
      }

      // Backwards compatibility: if endpoint exists but empty, try old status
      const fallback = await api.get("/email/oauth/status");
      const s = fallback.data?.status || null;

      if (s?.connected && s?.provider && s?.email) {
        setMailboxes([
          {
            provider: safeText(s.provider).toLowerCase(),
            email: safeText(s.email),
            displayName: safeText(s.displayName),
            connectedAt: s.connectedAt || null,
            isActive: true,
          },
        ]);
        setActiveMailboxProvider(safeText(s.provider).toLowerCase());
        setSendingMethod("oauth");
      } else {
        setMailboxes([]);
        setActiveMailboxProvider(null);
      }
    } catch (e) {
      // Backwards compatibility: if /mailboxes doesn't exist yet, fall back to /status
      try {
        const fallback = await api.get("/email/oauth/status");
        const s = fallback.data?.status || null;
        if (s?.connected && s?.provider && s?.email) {
          setMailboxes([
            {
              provider: safeText(s.provider).toLowerCase(),
              email: safeText(s.email),
              displayName: safeText(s.displayName),
              connectedAt: s.connectedAt || null,
              isActive: true,
            },
          ]);
          setActiveMailboxProvider(safeText(s.provider).toLowerCase());
          setSendingMethod("oauth");
        } else {
          setMailboxes([]);
          setActiveMailboxProvider(null);
        }
      } catch {
        setMailboxes([]);
        setActiveMailboxProvider(null);
      }
    } finally {
      setOauthLoading(false);
    }
  };

  const fetchInboxPermissionsSafe = async () => {
    setInboxPermissionsLoading(true);
    try {
      const res = await api.get("/email/inbox-permissions/status");
      setInboxPermissions(res.data || null);
    } catch (_) {
      setInboxPermissions(null);
    } finally {
      setInboxPermissionsLoading(false);
    }
  };

  const handleReconnectInboxPermissions = async (provider) => {
    const p = safeText(provider).toLowerCase();
    if (!p) return;
    setReconnectingInboxProvider(p);
    setError("");
    try {
      const res = await api.post(`/email/inbox-permissions/${encodeURIComponent(p)}/start`);
      if (res.data?.url) window.location.href = res.data.url;
      else setError("Could not start mailbox permission reconnect (missing url).");
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setReconnectingInboxProvider(null);
    }
  };

  const fetchSignatureSafe = async () => {
    setSignatureLoading(true);
    try {
      const res = await api.get("/email/signature");
      setSignature(safeText(res.data?.signature || ""));
    } catch (e) {
      setSignature("");
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    setError("");
    setSuccess("");
    try {
      const res = await api.get("/email/oauth/google/start");
      const url = res.data?.url;
      if (url) window.location.href = url;
      else setError("Could not start Google connection (missing url).");
    } catch (e) {
      setError(safeErrorText(e));
    }
  };

  const handleConnectMicrosoft = async () => {
    setError("");
    setSuccess("");
    try {
      const res = await api.get("/email/oauth/microsoft/start");
      const url = res.data?.url;
      if (url) window.location.href = url;
      else setError("Could not start Microsoft connection (missing url).");
    } catch (e) {
      setError(safeErrorText(e));
    }
  };

  /**
   * New endpoint idea:
   * POST /email/oauth/active  { provider: "google"|"microsoft" }
   * If endpoint isn't available yet, we still update local UI and show a message.
   */
  const handleSetActiveMailbox = async (provider) => {
    const p = safeText(provider).toLowerCase();
    if (!p) return;

    setSwitchingMailbox(true);
    setError("");
    setSuccess("");

    try {
      await api.post("/email/oauth/active", { provider: p });

      setMailboxes((prev) =>
        (prev || []).map((m) => ({
          ...m,
          isActive: safeText(m.provider).toLowerCase() === p,
        }))
      );
      setActiveMailboxProvider(p);
      setSuccess(`Active mailbox set to ${providerLabel(p)}.`);
    } catch (e) {
      // If backend not ready yet, still update UI locally to reduce confusion
      setMailboxes((prev) =>
        (prev || []).map((m) => ({
          ...m,
          isActive: safeText(m.provider).toLowerCase() === p,
        }))
      );
      setActiveMailboxProvider(p);
      setError(
        `Could not persist active mailbox selection yet. Backend endpoint missing or failed: ${safeErrorText(e)}`
      );
    } finally {
      setSwitchingMailbox(false);
    }
  };

  /**
   * New endpoint idea:
   * POST /email/oauth/disconnect { provider: "google"|"microsoft" }
   * Backwards compatibility:
   * - if provider param not supported, fallback to old /disconnect (disconnects everything)
   */
  const handleDisconnectProvider = async (provider) => {
    const p = safeText(provider).toLowerCase();
    const label = providerLabel(p);
    if (!window.confirm(`Disconnect ${label}? Emails will no longer be sent via this mailbox.`)) return;

    setDisconnectingProvider(p);
    setError("");
    setSuccess("");

    try {
      await api.post("/email/oauth/disconnect", { provider: p });

      // Remove it from list
      setMailboxes((prev) => (prev || []).filter((m) => safeText(m.provider).toLowerCase() !== p));

      // If it was active, clear active and pick another
      setActiveMailboxProvider((prevActive) => {
        if (safeText(prevActive).toLowerCase() === p) return null;
        return prevActive;
      });

      setSuccess(`${label} mailbox disconnected.`);
      await fetchMailboxesSafe();
    } catch (e) {
      // fallback to old behavior
      try {
        await api.post("/email/oauth/disconnect");
        setMailboxes([]);
        setActiveMailboxProvider(null);
        setSuccess(`Mailbox disconnected.`);
      } catch (e2) {
        setError(safeErrorText(e2));
      }
    } finally {
      setDisconnectingProvider(null);
    }
  };

  const handleSaveSignature = async () => {
    setSavingSignature(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/email/signature", { signature });
      setSuccess("Signature saved.");
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setSavingSignature(false);
    }
  };

  // ----------------------------
  // Domain config (existing)
  // ----------------------------
  const fetchConfig = async () => {
    setError("");
    try {
      const res = await api.get("/email/config");
      const cfg = res.data?.config || null;

      if (!cfg) {
        setConfigStatus("not_configured");
        setDomain("");
        setFromName("");
        setFromEmail("");
        setDnsRecords(null);
        setSenders([]);
        setTeamUsers([]);
        return;
      }

      setDomain(cfg.domain || "");
      setFromName(cfg.fromName || "");
      setFromEmail(cfg.fromEmail || "");
      setDnsRecords(cfg.dnsRecords || cfg?.dns_records || null);

      const authVerified = !!(
        cfg?.status?.authenticated === true ||
        cfg?.status?.authenticateResponse?.authenticated === true ||
        cfg?.authenticated === true
      );

      const dnsVerified = !!(
        cfg?.status?.verified === true ||
        cfg?.status?.validateResponse?.verified === true ||
        cfg?.verified === true
      );

      if (authVerified && dnsVerified) {
        setConfigStatus("verified");
        await Promise.all([fetchSendersSafe(), fetchTeamUsersSafe()]);
      } else {
        setConfigStatus("pending");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSendersSafe = async () => {
    setSendersLoading(true);
    try {
      const res = await api.get("/email/senders");
      setSenders(Array.isArray(res.data?.senders) ? res.data.senders : []);
    } catch (e) {
      setSenders([]);
    } finally {
      setSendersLoading(false);
    }
  };

  const fetchTeamUsersSafe = async () => {
    setTeamLoading(true);
    try {
      const res = await api.get("/user/organization/users");
      const list = Array.isArray(res.data?.users) ? res.data.users : [];
      setTeamUsers(list);
    } catch (e) {
      setTeamUsers([]);
    } finally {
      setTeamLoading(false);
    }
  };

  // ----------------------------
  // Domain flow
  // ----------------------------
  const handleStartSetup = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await api.post("/email/domain/setup", { domain, fromName, fromEmail });
      const cfg = res.data?.config;

      setDnsRecords(cfg?.dnsRecords || null);
      setConfigStatus("pending");
      setSuccess("Domain toegevoegd. Configureer nu de DNS records hieronder.");
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDomain = async () => {
    setChecking(true);
    setError("");
    setSuccess("");

    try {
      const res = await api.post("/email/domain/check");
      const cfg = res.data?.config;

      setDnsRecords(cfg?.dnsRecords || null);

      const authVerified = !!(
        cfg?.status?.authenticated === true ||
        cfg?.status?.authenticateResponse?.authenticated === true ||
        cfg?.authenticated === true
      );

      const dnsVerified = !!(
        cfg?.status?.verified === true ||
        cfg?.status?.validateResponse?.verified === true ||
        cfg?.verified === true
      );

      if (authVerified && dnsVerified) {
        setConfigStatus("verified");
        setSuccess("Domain succesvol geverifieerd 🎉 Emails worden nu verstuurd vanaf je domein.");
        await Promise.all([fetchSendersSafe(), fetchTeamUsersSafe()]);
      } else {
        setConfigStatus("pending");
        setError("Domain nog niet geverifieerd. DNS propagatie kan even duren.");
      }
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setChecking(false);
    }
  };

  const handleResetDomain = async () => {
    setResetting(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/email/reset");
      setConfigStatus("not_configured");
      setDomain("");
      setFromName("");
      setFromEmail("");
      setDnsRecords(null);
      setSenders([]);
      setTeamUsers([]);
      setSelectedUserId("");
      setSelectedSenderId("");
      setSuccess("Email sending instellingen zijn gereset.");
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setResetting(false);
    }
  };

  // ----------------------------
  // Sender profiles
  // ----------------------------
  const handleOpenAddSender = () => {
    setNewSenderName("");
    setNewSenderEmail("");
    setShowAddSender(true);
    setError("");
    setSuccess("");
  };

  const handleCreateSender = async () => {
    setAddingSender(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/email/senders", { fromName: newSenderName, fromEmail: newSenderEmail });
      setShowAddSender(false);
      setSuccess("Sender profiel toegevoegd.");
      await fetchSendersSafe();
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setAddingSender(false);
    }
  };

  const handleSetDefaultSender = async (senderId) => {
    setSettingDefaultSenderId(senderId);
    setError("");
    setSuccess("");
    try {
      await api.post(`/email/senders/${encodeURIComponent(senderId)}/default`);
      setSuccess("Default sender bijgewerkt.");
      await fetchSendersSafe();
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setSettingDefaultSenderId(null);
    }
  };

  const handleDeleteSender = async (senderId) => {
    if (!window.confirm("Deze sender verwijderen?")) return;
    setDeletingSenderId(senderId);
    setError("");
    setSuccess("");
    try {
      await api.delete(`/email/senders/${encodeURIComponent(senderId)}`);
      setSuccess("Sender verwijderd.");
      await fetchSendersSafe();
      await fetchTeamUsersSafe();
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setDeletingSenderId(null);
    }
  };

  const handleAssignSender = async () => {
    if (!selectedUserId || !selectedSenderId) return;
    setAssigning(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/email/senders/assign", { userId: selectedUserId, senderId: selectedSenderId });
      setSuccess("Sender toegewezen aan gebruiker.");
      await fetchTeamUsersSafe();
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setAssigning(false);
    }
  };

  // ----------------------------
  // Badges + styles
  // ----------------------------
  const renderDomainStatusBadge = () => {
    if (derivedStatus === "verified") {
      return (
        <span className="inline-flex items-center gap-2 text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full text-xs font-medium">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          Verified
        </span>
      );
    }
    if (derivedStatus === "pending") {
      return (
        <span className="inline-flex items-center gap-2 text-yellow-700 bg-yellow-50 border border-yellow-100 px-2 py-0.5 rounded-full text-xs font-medium">
          <span className="w-2 h-2 rounded-full bg-yellow-500" />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">
        <span className="w-2 h-2 rounded-full bg-gray-400" />
        Not configured
      </span>
    );
  };

  const renderOAuthBadge = () => {
    if (oauthLoading) {
      return (
        <span className="inline-flex items-center gap-2 text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">
          <span className="w-2 h-2 rounded-full bg-gray-400" />
          Checking…
        </span>
      );
    }
    if (anyMailboxConnected) {
      const active = activeMailbox?.provider ? providerLabel(activeMailbox.provider) : "Mailbox";
      return (
        <span className="inline-flex items-center gap-2 text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full text-xs font-medium">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          Connected ({active})
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">
        <span className="w-2 h-2 rounded-full bg-gray-400" />
        Not connected
      </span>
    );
  };

  const methodCardClass = (active) =>
    `border rounded-xl p-4 bg-white transition ${
      active ? "border-blue-200 ring-2 ring-blue-100" : "border-gray-200 hover:bg-gray-50"
    }`;

  const mailboxCardClass = (active) =>
    `border rounded-lg p-4 bg-white transition ${
      active ? "border-blue-200 ring-2 ring-blue-100" : "border-gray-200 hover:bg-gray-50"
    }`;

  // ----------------------------
  // UI
  // ----------------------------
  return (
    <div className="bg-white rounded-xl border p-6 max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Email sending</h3>
          <p className="text-sm text-gray-600 mt-1">
            Connect your mailbox (recommended) or authenticate your domain (advanced). We’ll always show exactly which
            address emails are sent from.
          </p>
        </div>
      </div>

      {inboxPermissions?.requiresReconnect && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-semibold text-amber-900">Inbox permission required</div>
          <div className="text-sm text-amber-800 mt-1">
            One or more connected mailboxes can send email but cannot yet read replies. Reconnect to grant inbox read access so Leadgenflow can detect replies and replan Agent outreach safely.
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(inboxPermissions.mailboxes || [])
              .filter((mailbox) => !mailbox.inboxReadEnabled)
              .map((mailbox) => {
                const p = safeText(mailbox.provider).toLowerCase();
                return (
                  <button
                    key={`${p}:${mailbox.email || 'mailbox'}`}
                    type="button"
                    onClick={() => handleReconnectInboxPermissions(p)}
                    disabled={reconnectingInboxProvider === p}
                    className="bg-amber-900 hover:bg-amber-950 disabled:bg-amber-400 text-white px-3 py-2 rounded-md text-sm"
                  >
                    {reconnectingInboxProvider === p
                      ? "Opening…"
                      : `Reconnect ${providerLabel(p)}${mailbox.email ? ` (${mailbox.email})` : ""}`}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {!inboxPermissions?.requiresReconnect && inboxPermissionsLoading && (
        <div className="text-xs text-gray-500">Checking inbox permissions…</div>
      )}

      {/* Current sending identity (always visible) */}
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-gray-800">Current sending identity</div>
            <div className="text-xs text-gray-600 mt-1">
              This is what recipients will see as the sender (From). Reply-To is where replies go.
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Mode:</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full border bg-white text-gray-700">
              {sendingMethod === "oauth" ? "Mailbox (OAuth)" : "Custom domain"}
            </span>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white border rounded-md p-3">
            <div className="text-xs font-semibold text-gray-600">From</div>
            <div className="text-sm text-gray-900 mt-1 break-all">{sendingFromLabel}</div>
          </div>

          <div className="bg-white border rounded-md p-3">
            <div className="text-xs font-semibold text-gray-600">Reply-To</div>
            <div className="text-sm text-gray-900 mt-1 break-all">{replyToLabel}</div>
          </div>
        </div>
      </div>

      {/* Choose method */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="font-medium text-gray-800">Choose your sending method</h4>
            <p className="text-sm text-gray-600 mt-1">
              Start with OAuth (Google/Microsoft). Use domain setup only if you need a shared sender domain for your team.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* OAuth card */}
          <button
            type="button"
            className={methodCardClass(sendingMethod === "oauth")}
            onClick={() => setSendingMethod("oauth")}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-left">
                <div className="text-sm font-semibold text-gray-900">Connect mailbox (recommended)</div>
                <div className="text-xs text-gray-600 mt-1">
                  Send emails directly from Gmail/Google Workspace or Microsoft 365/Outlook. No DNS required.
                </div>
              </div>
              <div className="pt-0.5">{renderOAuthBadge()}</div>
            </div>
          </button>

          {/* Domain card */}
          <button
            type="button"
            className={methodCardClass(sendingMethod === "domain")}
            onClick={() => setSendingMethod("domain")}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-left">
                <div className="text-sm font-semibold text-gray-900">Connect custom domain (advanced)</div>
                <div className="text-xs text-gray-600 mt-1">
                  Authenticate a domain/subdomain and manage sender profiles for your team.
                </div>
              </div>
              <div className="pt-0.5">{renderDomainStatusBadge()}</div>
            </div>
          </button>
        </div>
      </div>

      {/* OAUTH SECTION */}
      {sendingMethod === "oauth" && (
        <div className="border-t pt-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="font-medium mb-1">Mailbox connections</h4>
              <p className="text-sm text-gray-600">
                You can connect both Google and Microsoft. Then choose which mailbox is active for sending.
              </p>
            </div>

            <button
              type="button"
              onClick={() => Promise.all([fetchMailboxesSafe(), fetchInboxPermissionsSafe()])}
              disabled={oauthLoading || inboxPermissionsLoading}
              className="bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm border"
            >
              {oauthLoading ? "Refreshing…" : "Refresh status"}
            </button>
          </div>

          {/* Connect buttons (always available) */}
          <div className="rounded-lg border bg-gray-50 p-4">
            <div className="text-sm font-medium text-gray-800">Connect a mailbox</div>
            <div className="text-xs text-gray-600 mt-1">
              You can connect multiple providers. After connecting, set one as active.
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleConnectGoogle}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
              >
                Connect Google
              </button>

              <button
                type="button"
                onClick={handleConnectMicrosoft}
                className="bg-gray-900 hover:bg-black text-white px-4 py-2 rounded-md text-sm"
              >
                Connect Microsoft
              </button>
            </div>

            <div className="mt-3 text-xs text-gray-500">
              Tip: if your company requires admin approval, your IT admin may need to grant access.
            </div>
          </div>

          {/* Mailboxes list */}
          <div className="space-y-2">
            {oauthLoading ? (
              <div className="text-sm text-gray-600">Loading mailboxes…</div>
            ) : mailboxes.length === 0 ? (
              <div className="text-sm text-gray-500 bg-gray-50 border rounded p-3">
                No mailbox connected yet. Connect Google or Microsoft to send campaign emails from your own mailbox.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {mailboxes.map((m) => {
                  const p = safeText(m.provider).toLowerCase();
                  const isActive = !!m.isActive || (activeMailboxProvider && activeMailboxProvider === p);

                  return (
                    <div key={`${p}:${m.email || "unknown"}`} className={mailboxCardClass(isActive)}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800">
                            {providerLabel(p)}{" "}
                            {isActive && (
                              <span className="ml-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
                                Active
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-900 mt-1 break-all">
                            {m.email ? m.email : <span className="text-gray-500">Connected (email unknown)</span>}
                          </div>
                          {!!m.displayName && <div className="text-xs text-gray-500 mt-1">{m.displayName}</div>}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSetActiveMailbox(p)}
                            disabled={switchingMailbox || isActive}
                            className={`px-3 py-1.5 rounded-md text-sm border ${
                              isActive ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white hover:bg-gray-50 text-gray-800"
                            }`}
                          >
                            {switchingMailbox && !isActive ? "Setting…" : "Set active"}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDisconnectProvider(p)}
                            disabled={disconnectingProvider != null}
                            className="px-3 py-1.5 rounded-md text-sm border bg-white hover:bg-red-50 text-red-600 border-red-200"
                          >
                            {disconnectingProvider === p ? "Disconnecting…" : "Disconnect"}
                          </button>
                        </div>
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        When active, workflow emails are sent via {providerShortLabel(p)} (not Brevo).
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Signature */}
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h5 className="text-sm font-semibold text-gray-800">Email signature</h5>
                <p className="text-xs text-gray-600 mt-1">
                  This signature will be appended to workflow emails (recommended for consistency).
                </p>
              </div>
              <button
                type="button"
                onClick={handleSaveSignature}
                disabled={savingSignature}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm"
              >
                {savingSignature ? "Saving…" : "Save signature"}
              </button>
            </div>

            <div className="mt-3">
              <textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                rows={6}
                placeholder={
                  signatureLoading ? "Loading signature…" : "Best regards,\nJohn Doe\nCompany name\nPhone\nWebsite"
                }
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              />
              <div className="mt-2 text-xs text-gray-500">
                Later we can support HTML signatures + a preview, but this is a great first version.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DOMAIN SECTION */}
      {sendingMethod === "domain" && (
        <div className="border-t pt-6 space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="font-medium text-gray-800">Custom domain setup</h4>
              <p className="text-sm text-gray-600 mt-1">
                Authenticate a domain/subdomain. After verification, you can create sender profiles and assign them to team members.
              </p>
            </div>
            <div className="pt-1">{renderDomainStatusBadge()}</div>
          </div>

          {/* DOMAIN SETUP */}
          {canShowDomainSetupForm && (
            <div className="rounded-lg border bg-gray-50 p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">Domain</label>
                  <input
                    type="text"
                    placeholder={domainPlaceholder}
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: gebruik een subdomain zoals <code className="text-pink-600">mail.yourdomain.com</code>.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Default from name</label>
                  <input
                    type="text"
                    placeholder="Your Company"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Default from email</label>
                  <input
                    type="email"
                    placeholder="sales@yourdomain.com"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleStartSetup}
                  disabled={loading || !domain || !fromEmail}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm"
                >
                  {loading ? "Connecting…" : "Connect domain"}
                </button>

                <button
                  onClick={handleResetDomain}
                  disabled={resetting}
                  className="bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm border"
                >
                  {resetting ? "Resetting…" : "Reset"}
                </button>
              </div>
            </div>
          )}

          {/* DNS RECORDS */}
          {hasDomainConfig && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-medium mb-1">DNS records</h4>
                  <p className="text-sm text-gray-600">
                    Voeg deze records toe bij je DNS provider. Klik daarna op “Verify / Check domain”.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCheckDomain}
                    disabled={checking || !canCheckDomain}
                    className="bg-gray-900 hover:bg-black disabled:bg-gray-300 text-white px-4 py-2 rounded-md text-sm"
                  >
                    {checking ? "Checking…" : "Verify / Check domain"}
                  </button>

                  <button
                    onClick={handleResetDomain}
                    disabled={resetting}
                    className="bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm border"
                  >
                    {resetting ? "Resetting…" : "Reset"}
                  </button>
                </div>
              </div>

              {dnsDisplay.length > 0 ? (
                <div className="space-y-2">
                  {dnsDisplay.map((r, idx) => (
                    <div key={`${r.type}-${r.host}-${idx}`} className="bg-white border rounded-md p-3 text-sm">
                      <div className="flex flex-wrap gap-x-6 gap-y-1">
                        <div>
                          <span className="font-semibold">Type:</span> {r.type}
                        </div>
                        <div className="break-all">
                          <span className="font-semibold">Host:</span> {r.host}
                        </div>
                        <div className="break-all">
                          <span className="font-semibold">Value:</span> {r.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 bg-gray-50 border rounded p-3">
                  Geen DNS records gevonden. Klik “Verify / Check domain” om ze opnieuw op te halen.
                </div>
              )}
            </div>
          )}

          {/* SENDER PROFILES */}
          <div className="border-t pt-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-medium mb-1">Sender profiles</h4>
                <p className="text-sm text-gray-600">Maak meerdere “From” identities en wijs ze toe aan team members.</p>
              </div>

              <button
                onClick={handleOpenAddSender}
                disabled={!canShowSendersSection}
                className={`px-4 py-2 rounded-md text-sm ${
                  canShowSendersSection
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
              >
                + Add sender
              </button>
            </div>

            {!canShowSendersSection && (
              <div className="text-sm text-gray-500 bg-gray-50 border rounded p-3">
                Verifieer eerst je domein om sender profiles te gebruiken.
              </div>
            )}

            {canShowSendersSection && (
              <>
                {/* Sender list */}
                <div className="space-y-2">
                  {sendersLoading ? (
                    <div className="text-sm text-gray-600">Loading senders…</div>
                  ) : senders.length === 0 ? (
                    <div className="text-sm text-gray-500 bg-gray-50 border rounded p-3">
                      Nog geen sender profiles. Voeg er één toe om te starten.
                    </div>
                  ) : (
                    senders.map((s) => {
                      const id = safeText(s.id || s.senderId || s._id);
                      const isDefault = !!s.isDefault || !!s.default || id === safeText(senders?.defaultSenderId);

                      return (
                        <div key={id} className="border rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-800 truncate">
                                {safeText(s.fromName || s.name)}{" "}
                                <span className="text-gray-500">{`<${safeText(s.fromEmail || s.email)}>`}</span>
                              </div>

                              <div className="text-xs mt-1">
                                {isDefault ? (
                                  <span className="text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full">
                                    Default sender
                                  </span>
                                ) : (
                                  <span className="text-gray-500">Sender</span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => handleSetDefaultSender(id)}
                                disabled={!!settingDefaultSenderId || isDefault}
                                className={`px-3 py-1.5 rounded-md text-sm border ${
                                  isDefault
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white hover:bg-gray-50 text-gray-800"
                                }`}
                              >
                                {settingDefaultSenderId === id ? "Saving…" : "Set default"}
                              </button>

                              <button
                                onClick={() => handleDeleteSender(id)}
                                disabled={!!deletingSenderId || isDefault}
                                className={`px-3 py-1.5 rounded-md text-sm border ${
                                  isDefault
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white hover:bg-red-50 text-red-600"
                                }`}
                              >
                                {deletingSenderId === id ? "Deleting…" : "Delete"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Assignments overview + assign UI */}
                <div className="mt-6 border rounded-lg p-4 bg-gray-50 space-y-4">
                  <div>
                    <h5 className="text-sm font-semibold text-gray-800">Sender assignments</h5>
                    <p className="text-xs text-gray-600 mt-1">
                      Hier zie je welke sender standaard is toegewezen per team member. Je kunt dit hieronder aanpassen.
                    </p>
                  </div>

                  {/* Overview list */}
                  <div className="bg-white border rounded-md overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 border-b">
                      <div className="col-span-6">Team member</div>
                      <div className="col-span-6">Assigned sender</div>
                    </div>

                    {teamLoading ? (
                      <div className="px-3 py-3 text-sm text-gray-600">Loading team…</div>
                    ) : teamUsers.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-500">Geen team members gevonden.</div>
                    ) : (
                      teamUsers.map((u) => {
                        const assignedId = getAssignedSenderIdFromUser(u);
                        const senderObj = assignedId ? senderById.get(assignedId) : null;

                        const label = senderObj
                          ? `${safeText(senderObj.fromName || senderObj.name)} <${safeText(
                              senderObj.fromEmail || senderObj.email
                            )}>`
                          : "— (not assigned)";

                        return (
                          <div
                            key={u.userId || u._id}
                            className="grid grid-cols-12 gap-2 px-3 py-2 border-b last:border-b-0"
                          >
                            <div className="col-span-6 text-sm text-gray-800">
                              <div className="font-medium">{u.name}</div>
                              <div className="text-xs text-gray-500">{u.email}</div>
                            </div>
                            <div className="col-span-6 text-sm text-gray-800 flex items-center gap-2">
                              <span className={`${senderObj ? "text-gray-800" : "text-gray-500"}`}>{label}</span>
                              {senderObj?.isDefault && (
                                <span className="text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full text-xs">
                                  Default
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Assign form */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Team member</label>
                      <select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                        disabled={teamLoading}
                      >
                        <option value="">Select user…</option>
                        {teamUsers.map((u) => (
                          <option key={u.userId || u._id} value={u.userId || u._id}>
                            {u.name} ({u.email})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Sender</label>
                      <select
                        value={selectedSenderId}
                        onChange={(e) => setSelectedSenderId(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                      >
                        <option value="">Select sender…</option>
                        {senders.map((s) => {
                          const id = safeText(s.id || s.senderId || s._id);
                          return (
                            <option key={id} value={id}>
                              {safeText(s.fromName || s.name)} &lt;{safeText(s.fromEmail || s.email)}&gt;
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleAssignSender}
                      disabled={assigning || !selectedUserId || !selectedSenderId}
                      className="bg-gray-900 hover:bg-black disabled:bg-gray-300 text-white px-4 py-2 rounded-md text-sm"
                    >
                      {assigning ? "Assigning…" : "Assign sender"}
                    </button>

                    <div className="text-xs text-gray-500">
                      Tip: later kun je dit uitbreiden naar override per campaign of per workflow step.
                    </div>
                  </div>
                </div>

                {/* Add sender panel */}
                {showAddSender && (
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-semibold text-gray-800">Add sender</h5>
                      <button
                        onClick={() => setShowAddSender(false)}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Close
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">From name</label>
                        <input
                          type="text"
                          value={newSenderName}
                          onChange={(e) => setNewSenderName(e.target.value)}
                          className="w-full border rounded-md px-3 py-2 text-sm"
                          placeholder="Sales - John"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">From email</label>
                        <input
                          type="email"
                          value={newSenderEmail}
                          onChange={(e) => setNewSenderEmail(e.target.value)}
                          className="w-full border rounded-md px-3 py-2 text-sm"
                          placeholder="john@mail.yourdomain.com"
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={handleCreateSender}
                        disabled={addingSender || !newSenderEmail || !newSenderName}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm"
                      >
                        {addingSender ? "Adding…" : "Add sender"}
                      </button>

                      <button
                        onClick={() => setShowAddSender(false)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm border"
                      >
                        Cancel
                      </button>
                    </div>

                    <div className="text-xs text-gray-500 mt-2">
                      Gebruik een emailadres binnen je verified domein/subdomain voor beste deliverability.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer info */}
          <div className="text-xs text-gray-500 border-t pt-4">
            Je blijft eigenaar van je domein. Email sending kan altijd worden uitgezet door de DNS records te verwijderen.
          </div>
        </div>
      )}

      {/* Messages */}
      {error && <div className="text-sm text-red-600">{error}</div>}
      {success && <div className="text-sm text-green-600">{success}</div>}
    </div>
  );
}

export default EmailSendingSettings;
